import { useAppStore } from '../../../../store/appStore';
import { useMemo } from 'react';
import { Customer, GcpPlacementService, ScheduledFlight, Site } from '../../../../types';
import { useFilteredProjects } from './useFilteredProjects';
import { MissionData, ProjectData } from '../types';
import { MissionType } from '../../../../consts/missionType';
import { useUiStore } from '../../../../store/uiStore';
import { MissionTab } from '../../../../consts/missionTab';
import { determineIfScopeIsPanoOnly, determineIfScopeIsScan } from '../helpers/scope';
import { determineIsMissionCompleted } from '../helpers/completion';

type MissionsKeyedByDates = Record<string, MissionData[]>;

interface ScheduleResult {
  missionsKeyedByDates: MissionsKeyedByDates;
  sortedDates: string[];
  scheduledProjects: ProjectData[];
}

export const generateProjectId = (customer: Customer, site: Site): string => `${customer.id}-${site.id}`;

const doesScanMatchFilter = (
  missionFilter: MissionType | null,
  site: Site,
  { scope }: ScheduledFlight,
  gcpPlacementService?: GcpPlacementService,
) => {
  switch (missionFilter) {
    case MissionType.Panorama:
      return !!site.projectInfo.parsedPanoramaLocations?.length;
    case MissionType.Scan:
      return determineIfScopeIsScan(scope);
    case MissionType.GcpRequest:
      return !!gcpPlacementService;
    case MissionType.Other:
      return !determineIfScopeIsScan(scope) && !determineIfScopeIsPanoOnly(scope);
    default:
      return true;
  }
};

const splitMissionsByCompletion = (missionsKeyedByDates: MissionsKeyedByDates) => {
  const completedMissionsKeyedByDates: MissionsKeyedByDates = {};
  const uncompletedMissionsKeyedByDates: MissionsKeyedByDates = {};

  const completedScheduledProjects: ProjectData[] = [];
  const uncompletedScheduledProjects: ProjectData[] = [];

  for (const [date, missions] of Object.entries(missionsKeyedByDates)) {
    for (const mission of missions) {
      const { customer, site } = mission;
      const projectId = generateProjectId(customer, site);
      const isCompleted = determineIsMissionCompleted(mission);

      if (isCompleted) {
        if (!completedMissionsKeyedByDates[date]) {
          completedMissionsKeyedByDates[date] = [];
        }
        completedMissionsKeyedByDates[date].push(mission);

        if (!completedScheduledProjects.some((p) => generateProjectId(p.customer, p.site) === projectId)) {
          completedScheduledProjects.push({ customer, site });
        }
      } else {
        if (!uncompletedMissionsKeyedByDates[date]) {
          uncompletedMissionsKeyedByDates[date] = [];
        }
        uncompletedMissionsKeyedByDates[date].push(mission);

        if (!uncompletedScheduledProjects.some((p) => generateProjectId(p.customer, p.site) === projectId)) {
          uncompletedScheduledProjects.push({ customer, site });
        }
      }
    }
  }

  return {
    completedMissionsKeyedByDates,
    completedSortedDates: Object.keys(completedMissionsKeyedByDates).sort(),
    completedScheduledProjects,

    uncompletedMissionsKeyedByDates,
    uncompletedSortedDates: Object.keys(uncompletedMissionsKeyedByDates).sort(),
    uncompletedScheduledProjects,
  };
};

export const useFilteredMissions = (): ScheduleResult => {
  const { missions, missionFilter } = useAppStore();
  const { selectedMissionsTab } = useUiStore();
  const projects = useFilteredProjects();

  return useMemo(() => {
    if (!missions) {
      return {
        missionsKeyedByDates: {},
        sortedDates: [],
        scheduledProjects: [],
      };
    }

    const filteredProjectIds = new Set(projects.map((project) => generateProjectId(project.customer, project.site)));

    const scheduledProjects: ProjectData[] = [];
    const missionsKeyedByDates: MissionsKeyedByDates = {};
    const ignoreMissionFilter = selectedMissionsTab === MissionTab.Uploaded;

    missions.forEach((customer) => {
      customer.sites.forEach((site) => {
        const projectId = generateProjectId(customer, site);

        if (!filteredProjectIds.has(projectId)) {
          return;
        }

        site.scheduledFlights.forEach((schedule) => {
          const { flightDateIso } = schedule;

          const gcpPlacementService = site.gcpPlacementServices.find(
            (g) => g.servicesPerformedDateIso === flightDateIso,
          );

          if (!ignoreMissionFilter && !doesScanMatchFilter(missionFilter, site, schedule, gcpPlacementService)) {
            return;
          }

          if (!missionsKeyedByDates[flightDateIso]) {
            missionsKeyedByDates[flightDateIso] = [];
          }

          missionsKeyedByDates[flightDateIso].push({ customer, site, schedule, gcpPlacementService });
          if (!scheduledProjects.some((p) => generateProjectId(p.customer, p.site) === projectId)) {
            scheduledProjects.push({ customer, site });
          }
        });

        const showGcps = ignoreMissionFilter || !missionFilter || missionFilter === MissionType.GcpRequest;
        if (showGcps) {
          site.gcpPlacementServices.forEach((g) => {
            const { servicesPerformedDateIso } = g;

            if (!servicesPerformedDateIso) {
              return;
            }

            if (!missionsKeyedByDates[servicesPerformedDateIso]) {
              missionsKeyedByDates[servicesPerformedDateIso] = [];
            }

            const missionAlreadyExists = missionsKeyedByDates[servicesPerformedDateIso].some((scheduleData) => {
              return scheduleData.customer.id === customer.id && scheduleData.site.id === site.id;
            });

            if (!missionAlreadyExists) {
              missionsKeyedByDates[servicesPerformedDateIso].push({ customer, site, gcpPlacementService: g });
              if (!scheduledProjects.some((p) => generateProjectId(p.customer, p.site) === projectId)) {
                scheduledProjects.push({ customer, site });
              }
            }
          });
        }
      });
    });

    Object.values(missionsKeyedByDates).forEach((entries) => {
      entries.sort((a, b) => `${a.customer.name}${a.site.name}`.localeCompare(`${b.customer.name}${b.site.name}`));
    });

    const {
      completedMissionsKeyedByDates,
      completedSortedDates,
      completedScheduledProjects,

      uncompletedMissionsKeyedByDates,
      uncompletedSortedDates,
      uncompletedScheduledProjects,
    } = splitMissionsByCompletion(missionsKeyedByDates);

    if (selectedMissionsTab === MissionTab.Uploaded) {
      return {
        missionsKeyedByDates: completedMissionsKeyedByDates,
        sortedDates: completedSortedDates,
        scheduledProjects: completedScheduledProjects,
      };
    } else {
      return {
        missionsKeyedByDates: uncompletedMissionsKeyedByDates,
        sortedDates: uncompletedSortedDates,
        scheduledProjects: uncompletedScheduledProjects,
      };
    }
  }, [missions, projects, missionFilter, selectedMissionsTab]);
};
